import { Fragment, type ReactNode } from "react";
import { LatexText } from "@/components/LatexText";

/**
 * Renders the tiny markdown subset used by lesson blocks (see shared/lessonBlocks.ts):
 * **bold**, *italic*, `code`, line breaks, "- " bullet lines, and inline $KaTeX$.
 */

const INLINE = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`]+`)/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(INLINE).filter(p => p !== "");
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={key} className="font-bold"><LatexText text={part.slice(2, -2)} /></strong>;
    }
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return <em key={key}><LatexText text={part.slice(1, -1)} /></em>;
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return <code key={key} className="px-1.5 py-0.5 rounded bg-muted font-mono text-[0.9em]">{part.slice(1, -1)}</code>;
    }
    return <LatexText key={key} text={part} />;
  });
}

interface RichTextProps {
  text: string;
  className?: string;
  /** Render as a single inline span (no paragraphs/bullets). */
  inline?: boolean;
}

export function RichText({ text, className = "", inline = false }: RichTextProps) {
  if (!text) return null;
  if (inline) {
    return <span className={className}>{renderInline(text.replace(/\s*\n\s*/g, " "), "i")}</span>;
  }

  const lines = text.split(/\r?\n/);
  const nodes: ReactNode[] = [];
  let bullets: string[] = [];

  const flushBullets = (key: string) => {
    if (!bullets.length) return;
    nodes.push(
      <ul key={key} className="list-disc pl-6 space-y-1 my-1">
        {bullets.map((b, i) => <li key={i}>{renderInline(b, `${key}-${i}`)}</li>)}
      </ul>
    );
    bullets = [];
  };

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (/^\s*[-•]\s+/.test(line)) {
      bullets.push(line.replace(/^\s*[-•]\s+/, ""));
      return;
    }
    flushBullets(`ul-${idx}`);
    if (line.trim() === "") {
      nodes.push(<div key={`sp-${idx}`} className="h-2" />);
      return;
    }
    nodes.push(<p key={`p-${idx}`} className="leading-relaxed">{renderInline(line, `l-${idx}`)}</p>);
  });
  flushBullets("ul-end");

  return <div className={`space-y-1 ${className}`}>{nodes.map((n, i) => <Fragment key={i}>{n}</Fragment>)}</div>;
}
